import { PropertyFilterProps } from "@amzn/awsui-components-react";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import { compressFilters, decompressFilters } from "common/utils/compress-url";
import { buildingIdFormat } from "interfaces";
import { useSearchParams } from "react-router-dom";
import { UserPermissions } from "services/user-permissions";
import { convertTokensToPropertyFilter } from "common/utils/property-filter-helper";
import { LocationGroup, ORDER_STATUS_OPTIONS, OrderFilteringOptions, SortingState } from "./useOrderFiltering";
import { createKeywordFilters } from "./filtering-utils";

/**
 * Maps internal property paths to shortened URL parameter names.
 * Reduces URL length while maintaining filter state in shareable links.
 */
const PROPERTY_TO_URL_PARAM_MAP: Record<string, string> = {
    "items.subLineItems.poNumber": "po",
    "items.subLineItems.prNumber": "pr",
    orderReference: "order",
    "details.legacyOrderId": "legacy",
    "details.requestCategories": "category",
    display: "item",
    "details.deliverTo.region": "region",
    "details.deliverTo.country": "country",
    "details.deliverTo.city": "city",
    "details.deliverTo.building": "building",
};

/**
 * Location hierarchy keys used for filtering.
 * Order represents the hierarchical structure from broadest to most specific.
 */
const LOCATION_KEYS = ["region", "country", "city", "building"];

/**
 * Maximum URL length before compression is applied.
 * Prevents excessively long URLs that may cause browser or server issues.
 */
export const MAXIMUM_CHARACTERS_UNTIL_COMPRESSED = 500;

/**
 * Updates URL parameters to reflect current filter and sorting state.
 * Enables bookmarking and sharing of filtered views. Automatically compresses
 * URL when it exceeds MAXIMUM_CHARACTERS_UNTIL_COMPRESSED to prevent access issues.
 *
 * @param filterOptions {ReturnType<typeof OrderFilteringOptions>} - All current filter selections and configurations
 * @param selectedLocations {buildingIdFormat[]} - Memoized selected locations to avoid recalculation
 * @param [currentSorting] {SortingState} - Current sorting state for the table
 * @returns URLSearchParams object with encoded filter state
 */
export const updateUrlParams = (
    filterOptions: ReturnType<typeof OrderFilteringOptions>,
    selectedLocations: buildingIdFormat[],
    currentSorting?: SortingState,
): URLSearchParams => {
    const urlParams = new URLSearchParams();

    // Keywords
    const { keywords } = createKeywordFilters(filterOptions.query.tokens);
    if (keywords.length > 0) {
        urlParams.set("keywords", keywords.join(","));
    }

    // Requesters
    const requesters = filterOptions.requesterQuery.tokens
        .filter((token) => token.value?.length > 0)
        .map((token) => token.value.trim().toLowerCase());
    urlParams.set("requesters", requesters.join(","));

    // Status
    const selectedStatuses = filterOptions.orderStatus.filter((status) => status.selected).map((status) => status.id);
    urlParams.set("status", selectedStatuses.join(","));

    // Sorting
    if (currentSorting?.sortOrder && currentSorting?.sortBy) {
        urlParams.set("sortDirection", currentSorting.sortOrder);
        urlParams.set("sortBy", currentSorting.sortBy);
    }

    // Property filters
    const formattedQuery = convertTokensToPropertyFilter(filterOptions.query.tokens);
    Object.entries(formattedQuery).forEach(([key, values]) => {
        const urlKey = PROPERTY_TO_URL_PARAM_MAP[key] || key;
        if (values.length > 0) {
            urlParams.set(urlKey, values.join(","));
        }
    });

    urlParams.set("operation", filterOptions.query.operation);

    // Location filters
    const locationGroups = selectedLocations.reduce((acc, location) => {
        acc[location.type] = acc[location.type] || [];

        if (location.type === "city") {
            acc[location.type].push(`${location.name}|${location.root}`);
        } else {
            acc[location.type].push(location.name);
        }

        return acc;
    }, {} as LocationGroup);

    Object.entries(locationGroups).forEach(([type, names]) => {
        const urlKey = PROPERTY_TO_URL_PARAM_MAP[`details.deliverTo.${type}`];
        if (urlKey && names.length > 0) {
            urlParams.set(urlKey, names.join(","));
        }
    });

    // Compress URL if it exceeds maximum length to prevent access issues
    const paramsString = urlParams.toString();
    if (paramsString.length > MAXIMUM_CHARACTERS_UNTIL_COMPRESSED) {
        const compressed = new URLSearchParams();
        compressed.set("f", compressFilters(urlParams));
        return compressed;
    }

    return urlParams;
};

/**
 * Custom hook that provides URL parameter parsing functions for filter state.
 * Enables restoration of filter state from URL parameters on page load.
 * Handles both compressed and uncompressed URL formats.
 *
 * @returns Object containing parser functions for different filter types
 */
export const useUrlFilterParsers = () => {
    const [searchParams] = useSearchParams();
    const REVERSE_URL_KEYS = Object.fromEntries(
        Object.entries(PROPERTY_TO_URL_PARAM_MAP).map(([key, value]) => [value, key]),
    );

    /**
     * Retrieves the actual URL parameters, decompressing if necessary.
     * Checks for compressed filter parameter 'f' and decompresses it.
     *
     * @returns The URL search params in uncompressed format
     */
    const getActualParams = (): URLSearchParams => {
        const compressed = searchParams.get("f");
        return compressed ? decompressFilters(compressed) : searchParams;
    };

    /**
     * Parses property filter tokens and operation from URL parameters.
     * Reconstructs the search query state from URL-friendly format.
     * Excludes location keys as they are handled separately.
     *
     * @returns Property filter query object with tokens and operation
     */
    const parseQueryFromUrl = (): PropertyFilterProps.Query => {
        const actualParams = getActualParams();
        const tokens: PropertyFilterProps.Token[] = [];
        const keywords = actualParams.get("keywords");

        if (keywords) {
            keywords.split(",").forEach((keyword) => {
                tokens.push({ value: keyword.trim(), operator: ":" });
            });
        }

        Object.entries(REVERSE_URL_KEYS).forEach(([urlKey, propertyKey]) => {
            if (LOCATION_KEYS.includes(urlKey as any)) return;

            const values = actualParams.get(urlKey);
            if (values) {
                values.split(",").forEach((value) => {
                    tokens.push({ propertyKey, value: value.trim(), operator: "=" });
                });
            }
        });

        const operation = actualParams.get("operation") === "or" ? "or" : "and";
        return { tokens, operation };
    };

    /**
     * Parses requester filter from URL parameters.
     * Defaults to current user if no requesters specified in URL.
     *
     * @returns Property filter query object for requester filtering
     */
    const parseRequesterFromUrl = (): PropertyFilterProps.Query => {
        const actualParams = getActualParams();
        const requesters = actualParams.get("requesters");
        const tokens: PropertyFilterProps.Token[] = [];

        if (requesters === null) {
            tokens.push({ value: UserPermissions.instance.getUsername, operator: ":" });
            return { tokens, operation: "and" };
        }

        if (requesters) {
            requesters.split(",").forEach((requester) => {
                if (requester.trim()) {
                    tokens.push({ value: requester.trim(), operator: ":" });
                }
            });
        }

        return { tokens, operation: "and" };
    };

    /**
     * Parses order status selections from URL parameters.
     * Defaults to all statuses selected if no status parameter in URL.
     *
     * @param {CheckboxNode[]} - Array of checkbox nodes with selection state
     * @returns Array of checkbox nodes with selection state
     */
    const parseStatusFromUrl = (): CheckboxNode[] => {
        const actualParams = getActualParams();
        const statusParam = actualParams.get("status");

        if (statusParam === null) {
            return ORDER_STATUS_OPTIONS;
        }

        const selectedStatuses = statusParam ? statusParam.split(",") : [];
        return ORDER_STATUS_OPTIONS.map((status) => ({
            ...status,
            selected: selectedStatuses.includes(status.id),
        }));
    };

    /**
     * Parses location selections from URL parameters and applies to building hierarchy.
     * Handles hierarchical selection where parent selection cascades to children.
     * City locations include root path to distinguish cities with identical names.
     *
     * @param buildingsData {CheckboxNode[]} - Complete building hierarchy data
     * @returns Updated building hierarchy with selections applied
     */
    const parseLocationFromUrl = (buildingsData: CheckboxNode[]): CheckboxNode[] => {
        const actualParams = getActualParams();
        const hasLocationParams = LOCATION_KEYS.some((key) => actualParams.has(key));

        if (!hasLocationParams) {
            const unselectAll = (items: CheckboxNode[]): CheckboxNode[] =>
                items.map((item) => ({
                    ...item,
                    selected: false,
                    children: item.children ? unselectAll(Array.from(item.children)) : undefined,
                }));
            return unselectAll(buildingsData);
        }

        const selectedRegions = actualParams.get("region")?.split(",") || [];
        const selectedCountries = actualParams.get("country")?.split(",") || [];
        const selectedCities = actualParams.get("city")?.split(",") || [];
        const selectedBuildings = actualParams.get("building")?.split(",") || [];

        /**
         * Recursively updates location selection state in the hierarchy.
         * Parent selection cascades down to all children nodes.
         *
         * @param items {CheckboxNode[]} - Array of location nodes to update
         * @param [parentSelected=false] {boolean} - Whether parent node is selected
         * @returns Updated array with selection state applied
         */
        const updateLocationSelection = (items: CheckboxNode[], parentSelected = false): CheckboxNode[] => {
            return items.map((item) => {
                const locationData = JSON.parse(decodeURIComponent(item.id)) as buildingIdFormat;
                let isSelected = parentSelected;

                if (!isSelected) {
                    switch (locationData.type) {
                        case "region":
                            isSelected = selectedRegions.includes(locationData.name);
                            break;
                        case "country":
                            isSelected = selectedCountries.includes(locationData.name);
                            break;
                        case "city":
                            isSelected = selectedCities.some((cityEntry) => {
                                if (cityEntry.includes("|")) {
                                    const [cityName, rootPath] = cityEntry.split("|");
                                    return locationData.name === cityName && locationData.root === rootPath;
                                }
                                return locationData.name === cityEntry;
                            });
                            break;
                        case "building":
                            isSelected = selectedBuildings.includes(locationData.name);
                            break;
                    }
                }

                return {
                    ...item,
                    selected: isSelected,
                    children: item.children
                        ? updateLocationSelection(Array.from(item.children), isSelected)
                        : undefined,
                };
            });
        };

        return updateLocationSelection(buildingsData);
    };

    return {
        parseQueryFromUrl,
        parseRequesterFromUrl,
        parseStatusFromUrl,
        parseLocationFromUrl,
    };
};
